import { Fragment } from 'react';
import { SuitSymbol } from './SuitSymbol';

/**
 * Renders a bid token (`1C`, `1NT`, `2D`) with its suit drawn by the shared
 * `SuitSymbol` component, per the constitution's Suit Symbol Display standard —
 * suits must never be written as literal glyphs in page copy.
 *
 * Also handles free text containing tokens, so catalog strings such as
 * "The 1C opening — 16+ points" or "Responding to 1H/1S" render correctly
 * without each caller doing its own splitting.
 */

const TOKEN_PATTERN = /\b([1-7])(NT|C|D|H|S)\b/g;

export interface BidTokenProps {
  /** A single token, e.g. "1C". */
  bid: string;
  className?: string;
}

/** A single bid token: level digit plus suit symbol (or the letters "NT"). */
export function BidToken({ bid, className = '' }: BidTokenProps) {
  const match = /^([1-7])(NT|C|D|H|S)$/.exec(bid.trim().toUpperCase());
  if (!match) {
    return <span className={className}>{bid}</span>;
  }
  const level = match[1];
  const strain = match[2] as 'NT' | 'C' | 'D' | 'H' | 'S';
  return (
    <span className={className}>
      {level}
      {strain === 'NT' ? 'NT' : <SuitSymbol suit={strain} />}
    </span>
  );
}

export interface BidTextProps {
  /** Arbitrary text that may contain bid tokens to be symbol-rendered. */
  text: string;
  className?: string;
}

/** Free text with every embedded bid token rendered through `SuitSymbol`. */
export function BidText({ text, className = '' }: BidTextProps) {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // `TOKEN_PATTERN` is global, so reset before each use — a shared regex object
  // otherwise carries `lastIndex` between calls and silently skips matches.
  TOKEN_PATTERN.lastIndex = 0;
  while ((match = TOKEN_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(<BidToken key={`${match.index}-${match[0]}`} bid={match[0]} />);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return (
    <span className={className}>
      {parts.map((p, i) => (
        <Fragment key={i}>{p}</Fragment>
      ))}
    </span>
  );
}
